/**
 * Composer input box: text field, attachments, voice, send, and stop-current-turn.
 * While chatIsLoading, the send slot is a circular stop button.
 * After it is clicked, it shows that the cancellation request is in progress
 * until the active stream has actually ended.
 */
import React from "react";
import { Box, TextField, IconButton, Paper, Typography, CircularProgress, Link as MuiLink, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { getFontSize, FONT_SIZES } from "../utils/fontSizeUtils";
import researchReportJobsApi, { ResearchReportAttachmentOption } from "../apis/researchReportJobsApi";
import { encodeReportAttachmentToken } from "../shared/utils/reportAttachmentTokens";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useUserMsp } from "../hooks/useUserMsp";
import useThreadWait from "../hooks/useThreadWait";
import { canAccessAnalyticsAssistant } from "../utils/analyticsAssistantAccess";
import { getAttachmentContext } from "./inputAttachmentContext";

export interface ChatAttachment {
  filename: string;
  content_type: string;
  data: string;
  size_bytes?: number;
}

export interface ChatSendPayload {
  content: string;
  attachments?: ChatAttachment[];
}

export interface QueuedChatMessage {
  id: string;
  content: string;
  attachments?: ChatAttachment[];
}

interface InputBoxProps {
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  chatIsLoading: boolean;
  aiThreadId: string | null;
  handleSendMessage: (messageOverride?: string | ChatSendPayload) => void;
  queuedMessages?: QueuedChatMessage[];
  onDeleteQueuedMessage?: (id: string) => void;
  onUpdateQueuedMessage?: (id: string, content: string) => void;
  onSendQueuedMessageNow?: (id: string) => void;
  canSendQueuedMessagesNow?: boolean;
  /** Stop the in-flight HandleMessage turn. Shown in the send slot while chatIsLoading. */
  onStopTurn?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  showTypeahead?: boolean;
  hasNavigatedToTypeahead?: boolean;
  isEmbedded?: boolean;
  /** When true, input has no top border/shadow/radius so it visually merges with a drawer above */
  attachToDrawer?: boolean;
  fontSizes?: { input: number };
  /** Optional guard to ensure report attachment dropdown is limited to active MSP context. */
  currentMspId?: string | number | null;
  /** Optional preloaded report attachments (e.g. from Deep Research "Open in Chat Assistant"). */
  preloadedReportAttachments?: Array<{
    jobId: number;
    title: string;
    completedAt: string | null;
  }>;
  /** Optional thread id that must be active before preload chips are applied. */
  preloadForThreadId?: string | null;
  /** Called once preloaded report attachments have been consumed by this input. */
  onPreloadedReportAttachmentsConsumed?: () => void;
  /** Optional compact control rendered inside the prompt bar before the text input. */
  modeControl?: React.ReactNode;
  /** Explicitly enable/disable report attachments for this surface; defaults to feature-flag behavior. */
  enableReportAttachments?: boolean;
  /** Optional reason to disable the composer while keeping the user informed in-place. */
  disabledReason?: string | null;
  /**
   * The placeholder while the composer answers something (Theo's open question), in place of the
   * default one. A disabled reason still wins.
   */
  placeholder?: string | null;
}

function formatCompletedAt(value: string | null): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
}

function normalizeMspId(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

function buildTheoReportUrl(jobId: number): string | null {
  if (!Number.isFinite(Number(jobId)) || Number(jobId) < 1) return null;
  return `/report/${encodeURIComponent(String(jobId))}`;
}

type SelectedReportAttachment = {
  jobId: number;
  title: string;
  completedAt: string | null;
  token: string;
};

type SelectedAttachment = ChatAttachment & {
  id: string;
  previewUrl?: string;
};

const SUPPORTED_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"]);
const SUPPORTED_AUDIO_TYPES = new Set([
  "audio/flac",
  "audio/m4a",
  "audio/mp3",
  "audio/mp4",
  "audio/mpeg",
  "audio/mpga",
  "audio/ogg",
  "audio/vnd.wave",
  "audio/wav",
  "audio/wave",
  "audio/webm",
  "audio/x-m4a",
  "audio/x-wav",
  "video/mp4",
  "video/webm",
]);
const SUPPORTED_DOCUMENT_TYPES = new Set([
  "application/json",
  "application/pdf",
  "application/vnd.ms-outlook",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/xml",
  "application/x-yaml",
  "application/yaml",
  "message/rfc822",
  "text/calendar",
  "text/csv",
  "text/html",
  "text/markdown",
  "text/plain",
  "text/tab-separated-values",
  "text/xml",
  "text/yaml",
]);
const MAX_ATTACHMENTS = 4;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_AUDIO_BYTES = 25 * 1024 * 1024;
const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

type SpeechRecognitionResultItem = {
  isFinal: boolean;
  0: {
    transcript: string;
  };
};

type SpeechRecognitionEventLike = {
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultItem;
  };
};

type SpeechRecognitionErrorEventLike = {
  error?: string;
  message?: string;
};

type BrowserSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

type SpeechRecognitionWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  const speechWindow = window as SpeechRecognitionWindow;
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
}

function appendSpeechToText(baseText: string, speechText: string): string {
  const speech = speechText.trim();
  if (!speech) return baseText;
  const base = baseText.trimEnd();
  if (!base) return speech;
  return `${base} ${speech}`;
}

function formatBytes(bytes: number | undefined): string {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function getBase64Payload(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(",");
  return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
}

function inferAttachmentContentType(file: File): string {
  const explicit = String(file.type || "").split(";", 1)[0].trim().toLowerCase();
  if (explicit && explicit !== "application/octet-stream") return explicit;

  const lowerName = String(file.name || "").toLowerCase();
  if (/\.(png)$/i.test(lowerName)) return "image/png";
  if (/\.(jpe?g)$/i.test(lowerName)) return "image/jpeg";
  if (/\.(webp)$/i.test(lowerName)) return "image/webp";
  if (/\.(gif)$/i.test(lowerName)) return "image/gif";
  if (/\.(wav|wave)$/i.test(lowerName)) return "audio/wav";
  if (/\.(mp3|mpeg|mpga)$/i.test(lowerName)) return "audio/mpeg";
  if (/\.(m4a)$/i.test(lowerName)) return "audio/m4a";
  if (/\.(mp4)$/i.test(lowerName)) return "audio/mp4";
  if (/\.(ogg|oga)$/i.test(lowerName)) return "audio/ogg";
  if (/\.(flac)$/i.test(lowerName)) return "audio/flac";
  if (/\.(webm)$/i.test(lowerName)) return "audio/webm";
  if (/\.(eml)$/i.test(lowerName)) return "message/rfc822";
  if (/\.(pdf)$/i.test(lowerName)) return "application/pdf";
  if (/\.(docx)$/i.test(lowerName)) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (/\.(xlsx)$/i.test(lowerName)) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (/\.(txt|log)$/i.test(lowerName)) return "text/plain";
  if (/\.(md)$/i.test(lowerName)) return "text/markdown";
  if (/\.(csv)$/i.test(lowerName)) return "text/csv";
  if (/\.(tsv)$/i.test(lowerName)) return "text/tab-separated-values";
  if (/\.(json)$/i.test(lowerName)) return "application/json";
  if (/\.(xml)$/i.test(lowerName)) return "application/xml";
  if (/\.(ya?ml)$/i.test(lowerName)) return "application/yaml";
  if (/\.(html?|htm)$/i.test(lowerName)) return "text/html";
  return "";
}

function isAudioAttachment(item: Pick<ChatAttachment, "content_type">): boolean {
  return SUPPORTED_AUDIO_TYPES.has(String(item.content_type || "").split(";", 1)[0].trim().toLowerCase());
}

function readChatFileAsAttachment(file: File): Promise<SelectedAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Unable to read attachment"));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      if (!result) {
        reject(new Error("Unable to read attachment"));
        return;
      }
      const filename = file.name || `attachment-${Date.now()}`;
      const contentType = inferAttachmentContentType(file);
      resolve({
        id: `${filename}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        filename,
        content_type: contentType,
        data: getBase64Payload(result),
        size_bytes: file.size,
        previewUrl: SUPPORTED_IMAGE_TYPES.has(contentType) ? URL.createObjectURL(file) : undefined,
      });
    };
    reader.readAsDataURL(file);
  });
}

const InputBox: React.FC<InputBoxProps> = ({
  inputMessage,
  setInputMessage,
  chatIsLoading,
  aiThreadId,
  handleSendMessage,
  queuedMessages = [],
  onDeleteQueuedMessage,
  onUpdateQueuedMessage,
  onSendQueuedMessageNow,
  canSendQueuedMessagesNow = false,
  onStopTurn,
  inputRef,
  showTypeahead = false, // Default to false
  hasNavigatedToTypeahead = false, // Default to false
  isEmbedded = false,
  attachToDrawer = false,
  fontSizes,
  currentMspId = null,
  preloadedReportAttachments = [],
  preloadForThreadId = null,
  onPreloadedReportAttachmentsConsumed,
  modeControl,
  enableReportAttachments,
  disabledReason = null,
  placeholder = null,
}) => {
  const waitingForAction = useThreadWait(aiThreadId)?.stop_turn === true;
  const showStop = chatIsLoading || waitingForAction;
  const { userMspInfo } = useUserMsp();
  const flags = useFeatureFlag();
  const isReportAttachmentEnabled =
    enableReportAttachments ??
    canAccessAnalyticsAssistant(flags as Record<string, unknown>, userMspInfo?.role);
  const [loadingReports, setLoadingReports] = React.useState(false);
  const [reportOptions, setReportOptions] = React.useState<ResearchReportAttachmentOption[]>([]);
  const [selectedReportAttachments, setSelectedReportAttachments] = React.useState<SelectedReportAttachment[]>([]);
  const [selectedAttachments, setSelectedAttachments] = React.useState<SelectedAttachment[]>([]);
  const [editingQueuedId, setEditingQueuedId] = React.useState<string | null>(null);
  const [editingQueuedText, setEditingQueuedText] = React.useState("");
  const [highlightedAttachmentIndex, setHighlightedAttachmentIndex] = React.useState(0);
  const [voiceInputSupported, setVoiceInputSupported] = React.useState(false);
  const [isListening, setIsListening] = React.useState(false);
  const [isStoppingTurn, setIsStoppingTurn] = React.useState(false);
  const [voiceError, setVoiceError] = React.useState<string | null>(null);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const fetchedReportOptionsKeyRef = React.useRef<string | null>(null);
  const reportsRequestInFlightRef = React.useRef(false);
  const recognitionRef = React.useRef<BrowserSpeechRecognition | null>(null);
  const voiceBaseMessageRef = React.useRef("");
  const selectedAttachmentsRef = React.useRef<SelectedAttachment[]>([]);
  const inputDisabledReason = waitingForAction
    ? "Theo is waiting for the current operations to finish. You can stop the run."
    : typeof disabledReason === "string" && disabledReason.trim() ? disabledReason.trim() : null;
  const inputDisabled = Boolean(inputDisabledReason) || !aiThreadId;
  const attachmentContext = React.useMemo(
    () => (isReportAttachmentEnabled ? getAttachmentContext(inputMessage) : { mode: "none" as const }),
    [inputMessage, isReportAttachmentEnabled]
  );

  const normalizedCurrentMspId = React.useMemo(
    () => normalizeMspId(currentMspId),
    [currentMspId]
  );

  React.useEffect(() => {
    setVoiceInputSupported(Boolean(getSpeechRecognitionConstructor()));
  }, []);

  React.useEffect(() => {
    // The cancellation endpoint acknowledges the request before the task
    // worker and model stream have fully stopped. Keep the feedback visible
    // until the stream-end event clears chatIsLoading.
    if (!showStop) setIsStoppingTurn(false);
  }, [showStop]);

  const requestStopTurn = React.useCallback(() => {
    if (!onStopTurn || isStoppingTurn) return;
    setIsStoppingTurn(true);
    onStopTurn();
  }, [isStoppingTurn, onStopTurn]);

  React.useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    selectedAttachmentsRef.current = selectedAttachments;
  }, [selectedAttachments]);

  React.useEffect(() => {
    if (!editingQueuedId) return;
    const item = queuedMessages.find((queued) => queued.id === editingQueuedId);
    if (!item) {
      setEditingQueuedId(null);
      setEditingQueuedText("");
    }
  }, [editingQueuedId, queuedMessages]);

  React.useEffect(() => {
    return () => {
      selectedAttachmentsRef.current.forEach((attachment) => {
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      });
    };
  }, []);

  React.useEffect(() => {
    if (!isReportAttachmentEnabled) {
      setSelectedReportAttachments([]);
      setReportOptions([]);
      fetchedReportOptionsKeyRef.current = null;
      return;
    }
    // Reset report cache when MSP context changes.
    setReportOptions([]);
    fetchedReportOptionsKeyRef.current = null;
  }, [normalizedCurrentMspId, isReportAttachmentEnabled]);

  React.useEffect(() => {
    if (!isReportAttachmentEnabled) return;
    if (preloadForThreadId && String(aiThreadId || "") !== String(preloadForThreadId)) return;
    if (!Array.isArray(preloadedReportAttachments) || preloadedReportAttachments.length === 0) return;
    setSelectedReportAttachments((prev) => {
      const next = [...prev];
      for (const item of preloadedReportAttachments) {
        const jobId = Number(item.jobId);
        if (!Number.isFinite(jobId) || jobId < 1) continue;
        if (next.some((entry) => entry.jobId === jobId)) continue;
        const title = String(item.title || `Report ${jobId}`).trim() || `Report ${jobId}`;
        next.push({
          jobId,
          title,
          completedAt: item.completedAt ?? null,
          token: encodeReportAttachmentToken({
            jobId,
            title,
            completedAt: item.completedAt ?? null,
          }),
        });
      }
      return next;
    });
    onPreloadedReportAttachmentsConsumed?.();
  }, [
    isReportAttachmentEnabled,
    preloadForThreadId,
    aiThreadId,
    onPreloadedReportAttachmentsConsumed,
    preloadedReportAttachments,
  ]);

  React.useEffect(() => {
    if (attachmentContext.mode !== "report") return;
    const fetchKey = normalizedCurrentMspId || "__all__";
    if (fetchedReportOptionsKeyRef.current === fetchKey || reportsRequestInFlightRef.current) return;
    let cancelled = false;
    const loadReports = async () => {
      try {
        reportsRequestInFlightRef.current = true;
        setLoadingReports(true);
        const rows = await researchReportJobsApi.listAttachmentOptions(200);
        if (cancelled) return;
        const mspScopedRows = Array.isArray(rows) ? rows : [];
        mspScopedRows.sort((a, b) => {
          const aTime = new Date(a.completed_at || 0).getTime();
          const bTime = new Date(b.completed_at || 0).getTime();
          return bTime - aTime;
        });
        setReportOptions(mspScopedRows);
        fetchedReportOptionsKeyRef.current = fetchKey;
      } catch {
        if (!cancelled) setReportOptions([]);
        fetchedReportOptionsKeyRef.current = null;
      } finally {
        reportsRequestInFlightRef.current = false;
        setLoadingReports(false);
      }
    };
    void loadReports();
    return () => {
      cancelled = true;
    };
  }, [attachmentContext.mode, normalizedCurrentMspId]);

  const stopVoiceInput = () => {
    recognitionRef.current?.stop();
  };

  const startVoiceInput = () => {
    if (inputDisabled || chatIsLoading) return;
    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!SpeechRecognition) {
      setVoiceInputSupported(false);
      setVoiceError("Voice input is not supported in this browser.");
      return;
    }

    recognitionRef.current?.abort();
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";
    voiceBaseMessageRef.current = inputMessage;
    setVoiceError(null);

    recognition.onresult = (event) => {
      let transcript = "";
      for (let index = 0; index < event.results.length; index += 1) {
        transcript += event.results[index][0]?.transcript || "";
      }
      setInputMessage(appendSpeechToText(voiceBaseMessageRef.current, transcript));
    };
    recognition.onerror = (event) => {
      const error = event.error || event.message || "unknown";
      if (error === "not-allowed" || error === "service-not-allowed") {
        setVoiceError("Microphone access was blocked.");
      } else if (error !== "no-speech" && error !== "aborted") {
        setVoiceError("Voice input stopped. Please try again.");
      }
      setIsListening(false);
    };
    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
      inputRef?.current?.focus();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
    } catch {
      recognitionRef.current = null;
      setIsListening(false);
      setVoiceError("Voice input could not start.");
    }
  };

  const toggleVoiceInput = () => {
    if (isListening) {
      stopVoiceInput();
      return;
    }
    startVoiceInput();
  };

  const reportResults = React.useMemo(() => {
    if (attachmentContext.mode !== "report") return [];
    const q = attachmentContext.query;
    if (!q) return reportOptions;
    return reportOptions.filter((job) => {
      const hay = `${job.report_title || ""} ${job.job_id}`.toLowerCase();
      return hay.includes(q);
    });
  }, [attachmentContext, reportOptions]);

  const attachmentQuery = attachmentContext.mode === "none" ? "" : attachmentContext.query;

  const attachmentTypeResults = React.useMemo(
    () => ["report"].filter((item) => item.includes(attachmentContext.mode === "type" ? attachmentQuery : "")),
    [attachmentContext.mode, attachmentQuery]
  );

  React.useEffect(() => {
    setHighlightedAttachmentIndex(0);
  }, [attachmentContext.mode, attachmentQuery]);

  React.useEffect(() => {
    if (attachmentContext.mode === "report") {
      setHighlightedAttachmentIndex((prev) => {
        const maxIndex = Math.max(0, reportResults.length - 1);
        return Math.min(prev, maxIndex);
      });
      return;
    }
    if (attachmentContext.mode === "type") {
      setHighlightedAttachmentIndex((prev) => {
        const maxIndex = Math.max(0, attachmentTypeResults.length - 1);
        return Math.min(prev, maxIndex);
      });
    }
  }, [attachmentContext.mode, reportResults.length, attachmentTypeResults.length]);

  const applyAttachmentType = (type: "report") => {
    if (type !== "report") return;
    const next = inputMessage.replace(/(^|\s)@[a-z]*$/i, "$1@report ");
    setInputMessage(next);
  };

  const attachReport = async (job: ResearchReportAttachmentOption) => {
    const title = String(job.report_title || `Report ${job.job_id}`).trim();
    const token = encodeReportAttachmentToken({
      jobId: job.job_id,
      title,
      completedAt: job.completed_at,
    });
    setSelectedReportAttachments((prev) => {
      if (prev.some((item) => item.jobId === job.job_id)) return prev;
      return [
        ...prev,
        {
          jobId: job.job_id,
          title,
          completedAt: job.completed_at,
          token,
        },
      ];
    });
    // Remove @report query text after choosing one attachment.
    const next = inputMessage.replace(/(^|\s)@report(?:\s+[^\n@]*)?$/i, "$1").trimStart();
    setInputMessage(next);
  };

  const removeSelectedReportAttachment = (jobId: number) => {
    setSelectedReportAttachments((prev) => prev.filter((item) => item.jobId !== jobId));
  };

  const addAttachmentFiles = async (files: File[]) => {
    if (inputDisabled) return;
    const attachmentFiles = files.filter((file) => {
      const contentType = inferAttachmentContentType(file);
      return SUPPORTED_IMAGE_TYPES.has(contentType) || SUPPORTED_AUDIO_TYPES.has(contentType) || SUPPORTED_DOCUMENT_TYPES.has(contentType);
    });
    if (attachmentFiles.length === 0) return;

    setAttachmentError(null);
    const remainingSlots = MAX_ATTACHMENTS - selectedAttachmentsRef.current.length;
    if (remainingSlots <= 0) {
      setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }

    const accepted = attachmentFiles.slice(0, remainingSlots);
    if (attachmentFiles.length > remainingSlots) {
      setAttachmentError(`Only ${MAX_ATTACHMENTS} files can be attached at once.`);
    }

    const attachments: SelectedAttachment[] = [];
    for (const file of accepted) {
      const contentType = inferAttachmentContentType(file);
      const isImage = SUPPORTED_IMAGE_TYPES.has(contentType);
      const isAudio = SUPPORTED_AUDIO_TYPES.has(contentType);
      const isDocument = SUPPORTED_DOCUMENT_TYPES.has(contentType) || contentType.startsWith("text/");
      if (!isImage && !isAudio && !isDocument) {
        setAttachmentError("Only images, audio, EML, and common document/text files are supported.");
        continue;
      }
      const maxBytes = isAudio ? MAX_AUDIO_BYTES : isImage ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES;
      if (file.size > maxBytes) {
        setAttachmentError(`${isAudio ? "Audio files" : isImage ? "Images" : "Files"} must be ${formatBytes(maxBytes)} or smaller.`);
        continue;
      }
      try {
        attachments.push(await readChatFileAsAttachment(file));
      } catch {
        setAttachmentError("One attachment could not be read. Please try another file.");
      }
    }
    if (attachments.length > 0) {
      setSelectedAttachments((prev) => [...prev, ...attachments]);
    }
  };

  const removeSelectedAttachment = (id: string) => {
    setSelectedAttachments((prev) => {
      const removed = prev.find((item) => item.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const clearSelectedAttachments = () => {
    setSelectedAttachments((prev) => {
      prev.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return [];
    });
  };

  const startEditingQueuedMessage = (item: QueuedChatMessage) => {
    setEditingQueuedId(item.id);
    setEditingQueuedText(item.content);
  };

  const saveEditingQueuedMessage = () => {
    if (!editingQueuedId) return;
    const nextContent = editingQueuedText.trim();
    const item = queuedMessages.find((queued) => queued.id === editingQueuedId);
    const queuedAttachments = item?.attachments ?? [];
    const hasAttachments = queuedAttachments.length > 0;
    if (!nextContent && !hasAttachments) {
      onDeleteQueuedMessage?.(editingQueuedId);
    } else {
      onUpdateQueuedMessage?.(editingQueuedId, nextContent);
    }
    setEditingQueuedId(null);
    setEditingQueuedText("");
  };

  const cancelEditingQueuedMessage = () => {
    setEditingQueuedId(null);
    setEditingQueuedText("");
  };

  const buildSendPayload = (): string | ChatSendPayload => {
    const text = String(inputMessage || "");
    const trimmedText = text.trim();
    const attachments = selectedAttachments.map(({ filename, content_type, data, size_bytes }) => ({
      filename,
      content_type,
      data,
      size_bytes,
    }));
    const buildWithAttachments = (content: string): string | ChatSendPayload =>
      attachments.length > 0 ? { content, attachments } : content;
    if (!isReportAttachmentEnabled) return buildWithAttachments(trimmedText);
    const tokens = selectedReportAttachments.map((item) => item.token).join("\n");
    if (!tokens) return buildWithAttachments(trimmedText);
    if (!trimmedText) return buildWithAttachments(tokens);
    return buildWithAttachments(`${trimmedText}\n${tokens}`);
  };

  const hasSendableContent =
    inputMessage.trim().length > 0 ||
    selectedAttachments.length > 0 ||
    (isReportAttachmentEnabled && selectedReportAttachments.length > 0);

  const sendCurrentMessage = () => {
    if (inputDisabled || !hasSendableContent) return;
    if (isListening) stopVoiceInput();
    const payload = buildSendPayload();
    if (typeof payload === "string" ? !payload : !payload.content.trim() && !payload.attachments?.length) return;
    handleSendMessage(payload);
    // Clear attachment chips after send. Input text clear behavior remains with parent flow.
    setSelectedReportAttachments([]);
    clearSelectedAttachments();
    setAttachmentError(null);
  };

  const collapseReportContextToTypeContext = () => {
    const next = inputMessage.replace(/(^|\s)@report(?:\s+[^\n@]*)?$/i, "$1@");
    setInputMessage(next);
  };

  const collapseTypeContextToNone = () => {
    const next = inputMessage.replace(/(^|\s)@[a-z]*$/i, "$1");
    setInputMessage(next);
  };

  // Calculate font sizes based on embedded state
  const inputFontSize =
    fontSizes?.input ?? getFontSize(FONT_SIZES.INPUT, isEmbedded ?? false);

  const boxSx = attachToDrawer
    ? {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        p: 0,
        m: 0,
        background: "#FFFFFF",
        px: isEmbedded ? 2 : 3,
        py: isEmbedded ? 1 : 1.5,
        boxShadow: "none",
        border: "none",
        borderTop: "none",
        borderRadius: 0,
        minWidth: 0,
        position: "relative" as const,
      }
    : {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        p: 0,
        m: 0,
        background: "#FFFFFF",
        px: isEmbedded ? 2 : 3,
        py: isEmbedded ? 1 : 1.5,
        boxShadow: isEmbedded ? "none" : "0 8px 24px rgba(15, 23, 42, 0.07)",
        border: "1px solid #D9E0EA",
        borderTop: "1px solid #D9E0EA",
        borderRadius: isEmbedded ? 2 : "12px",
        minWidth: 0,
        position: "relative" as const,
      };

  return (
    <Box data-interactive="true" data-no-drag="true" sx={boxSx}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,audio/*,.wav,.mp3,.m4a,.ogg,.oga,.flac,.webm,.eml,.pdf,.docx,.xlsx,.txt,.md,.csv,.tsv,.json,.xml,.yaml,.yml,.html,.htm"
        multiple
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          void addAttachmentFiles(files);
          event.target.value = "";
        }}
      />
      {queuedMessages.length > 0 && (
        <Box
          sx={{
            mb: 0.9,
            display: "flex",
            flexDirection: "column",
            gap: 0.6,
          }}
        >
          {queuedMessages.map((item, index) => {
            const attachments = Array.isArray(item.attachments) ? item.attachments : [];
            const isEditing = editingQueuedId === item.id;
            const sendNowEnabled = Boolean(onSendQueuedMessageNow && canSendQueuedMessagesNow);
            return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minHeight: 36,
                  border: "1px solid #DDE3EC",
                  backgroundColor: "#F8FAFC",
                  borderRadius: 1,
                  px: 1,
                  py: 0.6,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#64748B",
                    fontWeight: 700,
                    minWidth: 22,
                    textAlign: "center",
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Typography>
                {isEditing ? (
                  <TextField
                    variant="standard"
                    value={editingQueuedText}
                    onChange={(event) => setEditingQueuedText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        saveEditingQueuedMessage();
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        cancelEditingQueuedMessage();
                      }
                    }}
                    fullWidth
                    multiline
                    maxRows={3}
                    autoFocus
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: 13,
                        lineHeight: 1.4,
                        fontFamily: "var(--theo-font-sans)",
                        color: "#263244",
                      },
                    }}
                  />
                ) : (
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        lineHeight: 1.4,
                        color: "#263244",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.content || attachments.map((attachment) => attachment.filename).join(", ")}
                    </Typography>
                    {attachments.length > 0 && (
                      <Typography
                        sx={{
                          mt: 0.15,
                          fontSize: 11,
                          color: "#64748B",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {attachments.map((attachment) => attachment.filename).join(", ")}
                      </Typography>
                    )}
                  </Box>
                )}
                {isEditing ? (
                  <Tooltip title="Save">
                    <IconButton
                      aria-label="Save queued message"
                      size="small"
                      onClick={saveEditingQueuedMessage}
                      sx={{ color: "#4F46E5", p: 0.35, flexShrink: 0 }}
                    >
                      <CheckIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title="Send now">
                      <span>
                        <IconButton
                          aria-label="Send queued message now"
                          size="small"
                          onClick={() => onSendQueuedMessageNow?.(item.id)}
                          disabled={!sendNowEnabled}
                          sx={{ color: "#4F46E5", p: 0.35, flexShrink: 0 }}
                        >
                          <SendIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        aria-label="Edit queued message"
                        size="small"
                        onClick={() => startEditingQueuedMessage(item)}
                        disabled={!onUpdateQueuedMessage}
                        sx={{ color: "#64748B", p: 0.35, flexShrink: 0 }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete queued message"
                    size="small"
                    onClick={() => onDeleteQueuedMessage?.(item.id)}
                    disabled={!onDeleteQueuedMessage}
                    sx={{ color: "#9F1D35", p: 0.35, flexShrink: 0 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}
      {selectedAttachments.length > 0 && (
        <Box
          sx={{
            mb: 0.75,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          {selectedAttachments.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                border: "1px solid #D1D5DB",
                backgroundColor: "#F9FAFB",
                borderRadius: 2,
                px: 0.75,
                py: 0.5,
                maxWidth: "100%",
                gap: 0.75,
              }}
            >
              {item.previewUrl ? (
                <Box
                  component="img"
                  src={item.previewUrl}
                  alt=""
                  sx={{
                    width: 28,
                    height: 28,
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "1px solid #E5E7EB",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    border: "1px solid #E5E7EB",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6B7280",
                    flexShrink: 0,
                  }}
                >
                  {isAudioAttachment(item) ? <AudiotrackIcon sx={{ fontSize: 16 }} /> : <AttachFileIcon sx={{ fontSize: 16 }} />}
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#374151",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 180,
                }}
              >
                {item.filename}{item.size_bytes ? ` (${formatBytes(item.size_bytes)})` : ""}
              </Typography>
              <IconButton
                size="small"
                onClick={() => removeSelectedAttachment(item.id)}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {isReportAttachmentEnabled && selectedReportAttachments.length > 0 && (
        <Box
          sx={{
            mb: 0.75,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          {selectedReportAttachments.map((item) => (
            <Box
              key={item.jobId}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                border: "1px solid #D1D5DB",
                backgroundColor: "#F9FAFB",
                borderRadius: "999px",
                px: 1.25,
                py: 0.5,
                maxWidth: "100%",
              }}
            >
              {(() => {
                const reportUrl = buildTheoReportUrl(item.jobId);
                if (reportUrl) {
                  return (
                    <MuiLink
                      href={reportUrl}
                      target="_blank"
                      rel="noreferrer"
                      underline="none"
                      sx={{
                        fontSize: 12,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        "&:hover": { color: "#111827" },
                      }}
                    >
                      {`report:${item.title}`}
                    </MuiLink>
                  );
                }
                return (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#374151",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {`report:${item.title}`}
                  </Typography>
                );
              })()}
              <IconButton
                size="small"
                onClick={() => removeSelectedReportAttachment(item.jobId)}
                sx={{ ml: 0.5, p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          width: "100%",
          minWidth: 0,
          gap: modeControl ? 0.75 : 0,
        }}
      >
        {modeControl ? (
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, pb: 0.35 }}>
            {modeControl}
          </Box>
        ) : null}
        <TextField
        variant="standard"
        placeholder={inputDisabledReason || placeholder || "How can I help you today?"}
        fullWidth
        multiline
        maxRows={4}
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData?.files || []);
          const imageFiles = files.filter((file) => String(file.type || "").toLowerCase().startsWith("image/"));
          if (imageFiles.length > 0) {
            e.preventDefault();
            void addAttachmentFiles(imageFiles);
          }
        }}
        onKeyDown={(e) => {
          const hasAttachmentDropdown = isReportAttachmentEnabled && attachmentContext.mode !== "none";

          if (hasAttachmentDropdown && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            const itemCount =
              attachmentContext.mode === "report" ? reportResults.length : attachmentTypeResults.length;
            if (itemCount > 0) {
              setHighlightedAttachmentIndex((prev) => {
                if (e.key === "ArrowDown") return (prev + 1) % itemCount;
                return (prev - 1 + itemCount) % itemCount;
              });
            }
            return;
          }

          if (hasAttachmentDropdown && e.key === "Escape") {
            e.preventDefault();
            if (attachmentContext.mode === "report") {
              collapseReportContextToTypeContext();
              return;
            }
            if (attachmentContext.mode === "type") {
              collapseTypeContextToNone();
              return;
            }
          }

          if (hasAttachmentDropdown && e.key === "Enter" && !e.shiftKey) {
            const itemCount =
              attachmentContext.mode === "report" ? reportResults.length : attachmentTypeResults.length;
            if (itemCount > 0) {
              e.preventDefault();
              if (attachmentContext.mode === "type") {
                const selectedType = attachmentTypeResults[highlightedAttachmentIndex];
                if (selectedType === "report") {
                  applyAttachmentType("report");
                }
                return;
              }
              if (attachmentContext.mode === "report") {
                const selectedReport = reportResults[highlightedAttachmentIndex];
                if (selectedReport) {
                  void attachReport(selectedReport);
                }
                return;
              }
            }
          }

          // If typeahead is visible, let it handle arrow keys
          if (showTypeahead && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
            return; // Let the typeahead handle arrow keys
          }

          // Handle Enter key for sending message (only if typeahead is not visible or user hasn't navigated)
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (
              !inputDisabled &&
              !hasNavigatedToTypeahead &&
              hasSendableContent
            ) {
              sendCurrentMessage();
            }
          }
        }}
        inputRef={inputRef}
        data-no-drag="true"
        disabled={inputDisabled}
        sx={{ flex: 1, minWidth: 0 }}
        InputProps={{
          disableUnderline: true,
          sx: {
            fontSize: `${inputFontSize}px`,
            lineHeight: "1.5",
            fontFamily: "var(--theo-font-sans)",
            color: "#263244",
            p: 0,
          },
          endAdornment: (
            <>
              <Tooltip title="Attach file">
                <span>
                  <IconButton
                    aria-label="Attach file"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={chatIsLoading || inputDisabled || selectedAttachments.length >= MAX_ATTACHMENTS}
                    data-no-drag="true"
                    sx={{
                      color: "#64748B",
                      width: isEmbedded ? 32 : 40,
                      height: isEmbedded ? 32 : 40,
                      ml: isEmbedded ? 0.25 : 0.75,
                      "&:hover": { backgroundColor: "#F1F5F9", color: "#334155" },
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: isEmbedded ? 18 : 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {voiceInputSupported ? (
                <Tooltip title={isListening ? "Stop voice input" : voiceError || "Use voice input"}>
                  <span>
                    <IconButton
                      aria-label={isListening ? "Stop voice input" : "Start voice input"}
                      onClick={toggleVoiceInput}
                      disabled={chatIsLoading || inputDisabled}
                      data-no-drag="true"
                      sx={{
                        color: isListening ? "#B91C1C" : voiceError ? "#B91C1C" : "#64748B",
                        width: isEmbedded ? 32 : 40,
                        height: isEmbedded ? 32 : 40,
                        ml: isEmbedded ? 0.25 : 0.75,
                        "&:hover": { backgroundColor: "#F1F5F9", color: "#334155" },
                      }}
                    >
                      {isListening ? (
                        <StopCircleIcon sx={{ fontSize: isEmbedded ? 18 : 22 }} />
                      ) : (
                        <MicIcon sx={{ fontSize: isEmbedded ? 18 : 22 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {showStop ? (
              <>
                {hasSendableContent && !inputDisabled ? (
                  <Tooltip title="Queue message">
                    <IconButton
                      aria-label="Queue message"
                      onClick={sendCurrentMessage}
                      data-no-drag="true"
                      sx={{
                        backgroundColor: "#EEF2FF",
                        color: "#5B3FC7",
                        borderRadius: "6px",
                        width: isEmbedded ? 32 : 40,
                        height: isEmbedded ? 32 : 40,
                        ml: isEmbedded ? 0.5 : 1,
                        "&:hover": {
                          backgroundColor: "#E0E7FF",
                        },
                      }}
                    >
                      <SendIcon sx={{ fontSize: isEmbedded ? 18 : 22 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title={isStoppingTurn ? "Stopping…" : "Stop"}>
                  <span>
                    <IconButton
                      aria-label={isStoppingTurn ? "Stopping" : "Stop"}
                      onClick={requestStopTurn}
                      disabled={!onStopTurn || isStoppingTurn}
                      data-no-drag="true"
                      sx={{
                        backgroundColor: "#8062D9",
                        color: "#fff",
                        borderRadius: "50%",
                        width: isEmbedded ? 32 : 40,
                        height: isEmbedded ? 32 : 40,
                        ml: isEmbedded ? 0.5 : 1,
                        boxShadow: "0 4px 10px rgba(128, 98, 217, 0.22)",
                        "&:hover": {
                          backgroundColor: "#6846BF",
                          boxShadow: "0 6px 14px rgba(128, 98, 217, 0.28)",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#8062D9",
                          color: "#fff",
                          opacity: 0.8,
                        },
                      }}
                    >
                      {isStoppingTurn ? (
                        <CircularProgress aria-label="Stopping" size={isEmbedded ? 16 : 20} color="inherit" />
                      ) : (
                        <Box
                          sx={{
                            width: isEmbedded ? 10 : 12,
                            height: isEmbedded ? 10 : 12,
                            bgcolor: "#fff",
                            borderRadius: "2px",
                          }}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </>
              ) : (
              <IconButton
                onClick={() => {
                  if (hasSendableContent && !chatIsLoading && !inputDisabled) {
                    sendCurrentMessage();
                  }
                }}
                disabled={!hasSendableContent || inputDisabled}
                data-no-drag="true"
                sx={{
                  backgroundColor: "#8062D9",
                  color: "#fff",
                  borderRadius: "6px",
                  width: isEmbedded ? 32 : 40,
                  height: isEmbedded ? 32 : 40,
                  ml: isEmbedded ? 0.5 : 1,
                  boxShadow: "0 4px 10px rgba(128, 98, 217, 0.22)",
                  "&:hover": {
                    backgroundColor: "#6846BF",
                    boxShadow: "0 6px 14px rgba(128, 98, 217, 0.28)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                <SendIcon sx={{ fontSize: isEmbedded ? 18 : 24 }} />
              </IconButton>
              )}
            </>
          ),
        }}
      />
      </Box>
      {voiceError ? (
        <Typography sx={{ mt: 0.75, fontSize: 12, color: "#B91C1C" }}>
          {voiceError}
        </Typography>
      ) : null}
      {attachmentError ? (
        <Typography sx={{ mt: 0.75, fontSize: 12, color: "#B91C1C" }}>
          {attachmentError}
        </Typography>
      ) : null}
      {isReportAttachmentEnabled && attachmentContext.mode !== "none" && (
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            left: isEmbedded ? 8 : 12,
            right: isEmbedded ? 8 : 12,
            bottom: "100%",
            mb: 1,
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            zIndex: 50,
            p: 0.5,
          }}
        >
          {attachmentContext.mode === "type" && (
            <Box>
              {attachmentTypeResults.map((item, index) => (
                  <Box
                    key={item}
                    onClick={() => applyAttachmentType("report")}
                    onMouseEnter={() => setHighlightedAttachmentIndex(index)}
                    sx={{
                      px: 1.25,
                      py: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      backgroundColor: index === highlightedAttachmentIndex ? "#F3F4F6" : "transparent",
                      "&:hover": { backgroundColor: "#F3F4F6" },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>@{item}</Typography>
                    <Typography sx={{ fontSize: 12, color: "#6B7280" }}>Attach research report</Typography>
                  </Box>
                ))}
            </Box>
          )}
          {attachmentContext.mode === "report" && (
            <Box>
              {loadingReports ? (
                <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography sx={{ fontSize: 12, color: "#6B7280" }}>Loading reports...</Typography>
                </Box>
              ) : (
                reportResults.map((job, index) => (
                  <Box
                    key={job.job_id}
                    onClick={() => void attachReport(job)}
                    onMouseEnter={() => setHighlightedAttachmentIndex(index)}
                    sx={{
                      px: 1.25,
                      py: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      backgroundColor: index === highlightedAttachmentIndex ? "#F3F4F6" : "transparent",
                      "&:hover": { backgroundColor: "#F3F4F6" },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {job.report_title || `Report ${job.job_id}`}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                      {formatCompletedAt(job.completed_at)}
                    </Typography>
                  </Box>
                ))
              )}
              {!loadingReports && reportResults.length === 0 && (
                <Typography sx={{ px: 1.5, py: 1, fontSize: 12, color: "#6B7280" }}>
                  No reports found.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default InputBox;
